/** One-shot interface sounds. These play in both demo and YouTube mode. */

import type { VoiceBus } from './graph.ts';

/** The thump and scratch of a stylus landing on a record. */
export function needleDrop({ ctx, sfx, noise }: VoiceBus): void {
  const when = ctx.currentTime + 0.01;

  const n = ctx.createBufferSource();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  n.buffer = noise;
  n.playbackRate.value = 0.6;
  f.type = 'bandpass';
  f.frequency.value = 900;
  f.Q.value = 1.2;
  g.gain.setValueAtTime(0.34, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.26);
  n.connect(f).connect(g).connect(sfx);
  n.start(when);
  n.stop(when + 0.3);

  const o = ctx.createOscillator();
  const og = ctx.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(120, when);
  o.frequency.exponentialRampToValueAtTime(48, when + 0.14);
  og.gain.setValueAtTime(0.22, when);
  og.gain.exponentialRampToValueAtTime(0.0001, when + 0.2);
  o.connect(og).connect(sfx);
  o.start(when);
  o.stop(when + 0.22);
}

/** A switch click. `pitch` distinguishes the deck's buttons from one another. */
export function click({ ctx, sfx }: VoiceBus, pitch = 1): void {
  const when = ctx.currentTime + 0.005;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  const f = ctx.createBiquadFilter();
  o.type = 'square';
  o.frequency.value = 1400 * pitch;
  f.type = 'bandpass';
  f.frequency.value = 2400 * pitch;
  f.Q.value = 1.5;
  g.gain.setValueAtTime(0.09, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.035);
  o.connect(f).connect(g).connect(sfx);
  o.start(when);
  o.stop(when + 0.05);
}

/** Spin-down wobble, used when the platter stops mid-record. */
export function powerDown({ ctx, sfx }: VoiceBus): void {
  const when = ctx.currentTime + 0.01;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(220, when);
  o.frequency.exponentialRampToValueAtTime(42, when + 0.75);
  g.gain.setValueAtTime(0.1, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.8);
  o.connect(g).connect(sfx);
  o.start(when);
  o.stop(when + 0.82);
}

/** The arm settling into the clip on its rest: a small, dry click. */
export function armRest({ ctx, sfx, noise }: VoiceBus): void {
  const when = ctx.currentTime + 0.01;

  const n = ctx.createBufferSource();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  n.buffer = noise;
  n.playbackRate.value = 1.1;
  f.type = 'bandpass';
  f.frequency.value = 2600;
  f.Q.value = 2.5;
  g.gain.setValueAtTime(0.16, when);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
  n.connect(f).connect(g).connect(sfx);
  n.start(when);
  n.stop(when + 0.06);

  const o = ctx.createOscillator();
  const og = ctx.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(240, when);
  o.frequency.exponentialRampToValueAtTime(110, when + 0.06);
  og.gain.setValueAtTime(0.1, when);
  og.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);
  o.connect(og).connect(sfx);
  o.start(when);
  o.stop(when + 0.09);
}
